/**
 * BookCoverArt — the single book-cover renderer for ScribbleReads. Used
 * everywhere a book is shown: Explore search results, Bookshelf, Profile,
 * Home dashboard sections, Communities, Landing. Given a book:
 *
 *  - a real `coverUrl` (from an Open Library search result, or already
 *    resolved) → the real image, object-cover, never distorted.
 *  - a real-world book ScribbleReads only knows the title/author of (seed
 *    /mock data with no `coverUrl` and no `source: 'openlibrary'`) → looks
 *    up a real cover via useBookCoverLookup, showing a skeleton while that
 *    resolves.
 *  - nothing found, still loading is skipped, or the image fails to load →
 *    the brand's gradient-spine fallback — a plain color block, no title
 *    text on top (Open Library is the only source of real cover art; when
 *    it doesn't have a book, we don't fake a cover with typography).
 *
 * Sizing/aspect ratio is entirely up to the caller's `className` (e.g.
 * `aspect-[2/3] w-full`), same as the components this supersedes.
 */

import React, { useState } from 'react';
import { cn as cx } from '@/lib/utils';
import { pickTone } from '@/lib/bookModel';
import { useBookCoverLookup } from '@/hooks/useBookSearch';

const SPINE_TONE = {
  coral: 'from-coral to-[#b85e58]',
  yellow: 'from-yellow to-[#e0b164]',
  sage: 'from-sage to-[#a9c39d]',
  blue: 'from-blue to-[#6b808b]',
};

export default function BookCoverArt({
  book,
  className = '',
  resolveMissingCover = true,
}) {
  const [imgFailed, setImgFailed] = useState(false);

  const author = book.author ?? book.authors?.[0] ?? null;
  const tone = SPINE_TONE[book.tone] ? book.tone : pickTone(book.id ?? book.title);

  // A book that already came from Open Library (search results, or an
  // earlier lookup) with no coverUrl genuinely has none there — don't
  // re-search for it. Only attempt a lookup for ScribbleReads's own
  // seed/mock books, which carry real titles/authors but no cover data.
  const alreadyFromOpenLibrary = book.source === 'openlibrary';
  const shouldResolve = resolveMissingCover && !book.coverUrl && !alreadyFromOpenLibrary && !!book.title;

  const { book: resolved, isLoading } = useBookCoverLookup({
    title: book.title,
    author,
    enabled: shouldResolve,
  });

  const coverUrl = book.coverUrl || resolved?.coverUrl;
  const showImage = !!coverUrl && !imgFailed;
  const showSkeleton = !showImage && shouldResolve && isLoading;

  if (showSkeleton) {
    return (
      <div
        className={cx('animate-pulse overflow-hidden rounded-r-md rounded-l-[4px] bg-line/60', className)}
        aria-hidden="true"
        data-testid="book-cover-skeleton"
      />
    );
  }

  return (
    <div
      className={cx(
        'relative overflow-hidden rounded-r-md rounded-l-[4px]',
        !showImage && `bg-gradient-to-br ${SPINE_TONE[tone]}`,
        className
      )}
    >
      {showImage ? (
        <img
          src={coverUrl}
          alt={author ? `${book.title} by ${author}` : book.title}
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <>
          <span className="absolute left-0 top-0 h-full w-2 bg-black/15" aria-hidden="true" />
          <span
            className="absolute right-3 top-0 h-9 w-6 rounded-b-[3px] bg-[#FFF7F0]/85"
            style={{ clipPath: 'polygon(0 0,100% 0,100% 100%,50% 82%,0 100%)' }}
            aria-hidden="true"
          />
        </>
      )}
    </div>
  );
}
